import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { themes as themesApi } from "../services/api";

export interface ThemePayload {
  sidebarBackground: string;
  sidebarTextColor: string;
  topbarBackground: string;
  topbarTextColor: string;
  primaryAccent: string;
  surface: string;
}

interface ThemeContextValue {
  theme: ThemePayload | null;
  setTheme: (payload: ThemePayload) => void;
}

const THEME_PAYLOAD_KEY = "nexora-theme-payload";

const ThemeContext = createContext<ThemeContextValue>({
  theme: null,
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const FALLBACK_HEX = "#1A6CF6";

// Data feeding this can come from localStorage (which may be stale/corrupted from
// an older version of this feature) or the backend, so never trust it's a valid
// "#rrggbb" string — fall back rather than crash the whole app on a bad value.
function safeHex(hex: string | undefined | null, fallback: string = FALLBACK_HEX): string {
  return typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : fallback;
}

function isLightHex(hex: string): boolean {
  const h = safeHex(hex).replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = safeHex(hex).replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function darkenHex(hex: string, amount: number): string {
  const h = safeHex(hex).replace("#", "");
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(parseInt(h.slice(0, 2), 16) * (1 - amount));
  const g = clamp(parseInt(h.slice(2, 4), 16) * (1 - amount));
  const b = clamp(parseInt(h.slice(4, 6), 16) * (1 - amount));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function applyTheme(payload: ThemePayload) {
  const root = document.documentElement.style;

  const topbarBackground = safeHex(payload.topbarBackground, "#0D1526");
  const topbarTextColor = safeHex(payload.topbarTextColor, "#F1F5F9");
  const sidebarBackground = safeHex(payload.sidebarBackground, "#0F1729");
  const sidebarTextColor = safeHex(payload.sidebarTextColor, "#94A3B8");
  const primaryAccent = safeHex(payload.primaryAccent);
  const surface = safeHex(payload.surface, "#F8FAFC");

  // Primary tokens
  root.setProperty("--nx-topbar-bg", topbarBackground);
  root.setProperty("--nx-topbar-text", topbarTextColor);
  root.setProperty("--nx-sidebar-bg", sidebarBackground);
  root.setProperty("--nx-sidebar-text", sidebarTextColor);
  root.setProperty("--nx-primary-accent", primaryAccent);
  root.setProperty("--nx-surface", surface);

  // Primary buttons (.nx-btn-primary) read --nx-grad-brand, not --nx-primary-accent
  // directly — derive a matching gradient so CTAs visibly reflect the chosen accent.
  root.setProperty(
    "--nx-grad-brand",
    `linear-gradient(135deg, ${primaryAccent} 0%, ${darkenHex(primaryAccent, 0.35)} 100%)`,
  );

  // Sidebar derived tokens — adapt to light vs dark sidebar
  const sidebarLight = isLightHex(sidebarBackground);
  root.setProperty(
    "--nx-sidebar-text-muted",
    sidebarLight ? "rgba(55,65,81,0.45)" : "rgba(255,255,255,0.22)",
  );
  root.setProperty(
    "--nx-sidebar-active-text",
    sidebarLight ? primaryAccent : "#ffffff",
  );
  root.setProperty(
    "--nx-sidebar-item-active-bg",
    hexToRgba(primaryAccent, 0.1),
  );
  root.setProperty(
    "--nx-sidebar-item-hover-bg",
    sidebarLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.05)",
  );
  root.setProperty(
    "--nx-sidebar-item-hover-text",
    sidebarLight ? "#111827" : "rgba(255,255,255,0.8)",
  );
  root.setProperty(
    "--nx-sidebar-border",
    sidebarLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.05)",
  );

  // Topbar derived tokens — adapt to light vs dark topbar
  const topbarLight = isLightHex(topbarBackground);
  root.setProperty(
    "--nx-topbar-border",
    topbarLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)",
  );
  root.setProperty(
    "--nx-topbar-text-subtle",
    topbarLight ? "rgba(55,65,81,0.5)" : "rgba(255,255,255,0.35)",
  );
  root.setProperty(
    "--nx-topbar-input-bg",
    topbarLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
  );
  root.setProperty(
    "--nx-topbar-input-border",
    topbarLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
  );
  root.setProperty(
    "--nx-topbar-icon-color",
    topbarLight ? "rgba(55,65,81,0.6)" : "rgba(255,255,255,0.6)",
  );
  root.setProperty(
    "--nx-topbar-button-bg",
    topbarLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
  );
  root.setProperty(
    "--nx-topbar-button-border",
    topbarLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.16)",
  );
}

const PAYLOAD_KEYS: (keyof ThemePayload)[] = [
  "sidebarBackground",
  "sidebarTextColor",
  "topbarBackground",
  "topbarTextColor",
  "primaryAccent",
  "surface",
];

function isValidPayload(value: unknown): value is ThemePayload {
  return (
    !!value &&
    typeof value === "object" &&
    PAYLOAD_KEYS.every((key) => typeof (value as Record<string, unknown>)[key] === "string")
  );
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemePayload | null>(() => {
    try {
      const stored = window.localStorage.getItem(THEME_PAYLOAD_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isValidPayload(parsed)) return parsed;
        window.localStorage.removeItem(THEME_PAYLOAD_KEY);
      }
    } catch {
      // ignore
    }
    return null;
  });

  useEffect(() => {
    if (theme) applyTheme(theme);
  }, [theme]);

  const { user } = useAuth();

  // The theme saved to localStorage paints instantly on load; once the society's
  // server-persisted active theme resolves, it takes over as the source of truth
  // so the theme is consistent across devices/browsers, not just this one.
  useEffect(() => {
    if (!user?.societyId) return;
    themesApi
      .getActive(user.societyId)
      .then((active) => {
        if (!active) return;
        const payload: ThemePayload = {
          sidebarBackground: active.sidebarBackground,
          sidebarTextColor: active.sidebarTextColor,
          topbarBackground: active.topbarBackground,
          topbarTextColor: active.topbarTextColor,
          primaryAccent: active.primaryAccent,
          surface: active.surface,
        };
        setThemeState(payload);
        applyTheme(payload);
        try {
          window.localStorage.setItem(THEME_PAYLOAD_KEY, JSON.stringify(payload));
        } catch {
          // ignore storage failures
        }
      })
      .catch(() => {
        // no active theme saved server-side yet, or request failed — keep local/default
      });
  }, [user?.societyId]);

  const setTheme = (payload: ThemePayload) => {
    setThemeState(payload);
    applyTheme(payload);
    try {
      window.localStorage.setItem(THEME_PAYLOAD_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
